import React, { useEffect, useRef, useState } from "react";
import {
  BadgeDollarSign,
  CheckCircle2,
  type LucideIcon,
  MoreHorizontal,
  Megaphone,
  Save,
  Target,
  Truck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { ModalWrapper } from "@/components/modals/ModalWrapper";
import { dictionary } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency } from "../company/utils";
import type { Funnel, FunnelMetaStatus } from "./types";
import { MatchCampaignsModal } from "./MatchCampaignsModal";
import { financeService } from "../../../services/financeService";

interface FunnelCardProps {
  funnel: Funnel;
  sequence: number;
}

const getFunnelSequence = (id: string) => {
  const sequence = id.match(/\d+$/)?.[0];
  return sequence ?? id;
};

const formatCurrencyMetric = (value: number | null) => {
  if (value === null) return "--";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    const sign = value < 0 ? "-" : "";
    return `${sign}$${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 1 }).format(abs / 1_000_000)}M`;
  }
  return formatCurrency(value);
};
const getMetricValueColorClass = (value: number | null) => {
  return value === null ? "text-gray-300" : "text-gray-900";
};

const getStatusTooltip = (status: FunnelMetaStatus, campaignCount: number) => {
  if (status === "unconfigured") return "Meta: no conectado";
  if (status === "configured_no_data") return "Meta: conectado, sin datos en este periodo";
  if (status === "configured_full") return `Meta: ${campaignCount} campañas conectadas Dropi: conectado`;
  return `Meta: ${campaignCount} campañas conectadas Dropi: pendiente`;
};

export const FunnelCard: React.FC<FunnelCardProps> = ({ funnel, sequence }) => {
  const { language } = useAuth();
  const navigate = useNavigate();
  const [metaModalOpen, setMetaModalOpen] = useState(false);
  const [logisticsModalOpen, setLogisticsModalOpen] = useState(false);
  const [connectionsMenuOpen, setConnectionsMenuOpen] = useState(false);
  const [manualLogisticsConfigured, setManualLogisticsConfigured] = useState(funnel.manualLogisticsConfigured);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const idHtml = "";
  const image = funnel.productImageUrl;
  const isActive = funnel.isActive;
  const isArchived = Boolean(funnel.isArchived);
  const nameFunnel = funnel.name;
  const productName = funnel.productName;
  const fetchedProductName = funnel.productName;
  const brandName = funnel.name;
  const idSequence = funnel.idSequenceOwnerId ?? sequence ?? getFunnelSequence(funnel.id);
  const showControls = true;
  const profitValue = funnel.metrics.profit;
  const salesValue = funnel.metrics.salesValue;
  const cpaValue = funnel.metrics.cpa;
  const metaConnected = funnel.matchedCampaignIds.length > 0 || funnel.metaStatus !== "unconfigured";
  const logisticsConfigured = funnel.metaStatus === "configured_full" || manualLogisticsConfigured;

  useEffect(() => {
    setManualLogisticsConfigured(funnel.manualLogisticsConfigured);
  }, [funnel.manualLogisticsConfigured]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setConnectionsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDetailClick = (view: "price" | "cpa" | "logistics" | "marketing") => {
    const tab = view === "marketing" ? "marketing" : "finance";
    navigate(`/finance/funnels/${funnel.id}?tab=${tab}&view=${view}`);
  };

  return (
    <>
      <div
        id={idHtml}
        className={`flex flex-col justify-between rounded-[20px] shadow-sm border border-gray-200 overflow-hidden transition-all duration-500 ${!showControls ? "bg-white/70" : "bg-white"
          }`}
      >
        <div className="p-6">
        <div className="flex items-start gap-3 border-b border-gray-100 pb-4">
          <div className="relative w-[84px] h-[84px] rounded-lg shadow-sm border border-gray-200 overflow-hidden flex-shrink-0">
            <img
              src={image || "/assets/images/product-placeholder.png"}
              alt={productName || nameFunnel}
              className="w-full h-full object-cover"
            />
            {!image && (
              <div className="absolute inset-0 overflow-hidden">
                <div
                  className="absolute inset-0 animate-shine"
                  style={{
                    background: "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.6) 50%, transparent 70%)",
                    width: "200%",
                    height: "100%",
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div className="flex flex-col">
                {!showControls ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500 mb-1 self-start animate-pulse">
                    {dictionary["processing"][language]}
                  </span>
                ) : (
                  <span
                    title={isArchived ? "Embudo eliminado con historial financiero" : undefined}
                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                      isArchived
                        ? "bg-gray-100 text-gray-600"
                        : isActive
                          ? "bg-[#DCFCE7] text-[#15803D]"
                          : "bg-gray-100 text-gray-600"
                      } mb-1 self-start`}
                  >
                    {!isArchived && isActive && (
                      <span className="w-1 h-1 rounded-full bg-[#15803D] mr-1"></span>
                    )}
                    {isArchived
                      ? "Eliminado"
                      : isActive
                      ? dictionary["active"][language]
                      : dictionary["draft"][language]}
                  </span>
                )}
                <h3 className="text-[1rem] leading-[1.4rem] font-semibold text-gray-900">
                  {brandName}
                </h3>
                {(fetchedProductName || productName) && (
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                    {fetchedProductName || productName}
                  </p>
                )}
              </div>
              {showControls ? (
                <div className="relative">
                  <button
                    ref={menuButtonRef}
                    type="button"
                    onClick={() => setConnectionsMenuOpen((value) => !value)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Configurar conexiones del embudo"
                    title="Configurar conexiones"
                  >
                    <MoreHorizontal className="h-5 w-5" />
                  </button>
                  <div
                    ref={menuRef}
                    className={`absolute right-0 top-9 z-20 w-64 origin-top-right rounded-xl border border-gray-200 bg-white py-2 shadow-xl transition-all duration-200 ${
                      connectionsMenuOpen ? "scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0"
                    }`}
                  >
                    <p className="border-b border-gray-100 px-4 pb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      Conexiones del embudo
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setConnectionsMenuOpen(false);
                        setMetaModalOpen(true);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 transition hover:bg-gray-50"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                        <Megaphone className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold">Campañas Meta</span>
                        <span className="block truncate text-xs text-gray-500">
                          {metaConnected ? `${funnel.matchedCampaignIds.length} conectadas` : "Seleccionar campañas"}
                        </span>
                      </span>
                      {metaConnected ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : null}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setConnectionsMenuOpen(false);
                        setLogisticsModalOpen(true);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 transition hover:bg-gray-50"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                        <Truck className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold">Logística</span>
                        <span className="block truncate text-xs text-gray-500">
                          {logisticsConfigured ? "Datos disponibles" : "Configurar manualmente"}
                        </span>
                      </span>
                      {logisticsConfigured ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : null}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="text-[.675rem] leading-[1.25rem] text-gray-600 font-semibold mb-1">
            {dictionary["funnel"][language]} #{idSequence}
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="min-w-0">
              <p className="mb-1 text-xs text-gray-500">Utilidad</p>
              <p className={`text-base font-bold truncate ${getMetricValueColorClass(profitValue)}`}>
                {formatCurrencyMetric(profitValue)}
              </p>
            </div>
            <div className="min-w-0">
              <p className="mb-1 text-xs text-gray-500">Ventas</p>
              <p className={`text-base font-bold truncate ${getMetricValueColorClass(salesValue)}`}>
                {formatCurrencyMetric(salesValue)}
              </p>
            </div>
            <div className="min-w-0">
              <p className="mb-1 text-xs text-gray-500">CPA</p>
              <p className={`text-base font-bold truncate ${getMetricValueColorClass(cpaValue)}`}>
                {formatCurrencyMetric(cpaValue)}
              </p>
            </div>
          </div>
        </div>
        </div>

        {showControls ? (
          <div>
            <div className="px-6 pb-3 overflow-x-hidden">
              <div className="flex items-center gap-2.5">
                <span className="sr-only">{getStatusTooltip(funnel.metaStatus, funnel.matchedCampaignIds.length)}</span>
                <FunnelInsightButton
                  label="Precio"
                  title="Precio ideal"
                  description="Calcula y proyecta tu precio de venta."
                  icon={BadgeDollarSign}
                  onClick={() => handleDetailClick("price")}
                />
                <FunnelInsightButton
                  label="CPA"
                  title="CPA ideal"
                  description="Entiende tu CPA ideal y cómo mejorarlo."
                  icon={Target}
                  onClick={() => handleDetailClick("cpa")}
                />
                <FunnelInsightButton
                  label="Logística"
                  title="Logística"
                  description="Próximamente podrás revisar costos, estados y riesgos logísticos del embudo."
                  icon={Truck}
                  onClick={() => handleDetailClick("logistics")}
                />
                <FunnelInsightButton
                  label="Marketing"
                  title="Marketing"
                  description="Próximamente podrás ver oportunidades de pauta y lectura creativa del embudo."
                  icon={Megaphone}
                  onClick={() => handleDetailClick("marketing")}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="px-6 pb-6">
            <div className="flex items-center justify-center gap-3 h-12 bg-white/80 rounded-full px-4 border border-gray-200">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin shrink-0" />
              <span className="text-sm text-gray-500 font-medium">
                {dictionary["your-funnel-will-be-ready-soon"]?.[language] ?? "Your funnel will be ready in a moment"}
              </span>
            </div>
          </div>
        )}
      </div>

      <MatchCampaignsModal
        open={metaModalOpen}
        onClose={() => setMetaModalOpen(false)}
        funnelId={funnel.id}
        funnelName={funnel.name}
      />
      <ManualLogisticsModal
        open={logisticsModalOpen}
        onClose={() => setLogisticsModalOpen(false)}
        funnelId={funnel.id}
        funnelName={funnel.name}
        onSaved={(configured) => setManualLogisticsConfigured(configured)}
      />
    </>
  );
};

function FunnelInsightButton({
  label,
  title,
  description,
  icon: Icon,
  onClick,
}: {
  label: string;
  title: string;
  description: string;
  icon: LucideIcon;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="group relative flex items-center overflow-visible rounded-full border border-gray-200 bg-white p-3 text-gray-500 transition duration-[400ms] ease-in-out hover:bg-gray-50 hover:pr-3.5 hover:text-gray-800"
    >
      <Icon size={18} className="shrink-0" />
      <span className="max-w-0 overflow-hidden whitespace-nowrap text-xs font-medium transition-all duration-[400ms] ease-in-out group-hover:ml-1.5 group-hover:max-w-[6rem]">
        {label}
      </span>
      <span className="pointer-events-none absolute bottom-[calc(100%+10px)] left-0 z-20 hidden w-60 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left shadow-xl group-hover:block">
        <span className="block text-xs font-bold text-gray-900">{title}</span>
        <span className="mt-1 block text-xs font-medium leading-5 text-gray-500">{description}</span>
      </span>
    </button>
  );
}

type ManualLogisticsDraft = {
  productCost: number;
  shippingCost: number;
  fulfillment: number;
  platformCommission: number;
  returnsRate: number;
  currency: string;
};

const emptyDraft: ManualLogisticsDraft = {
  productCost: 0,
  shippingCost: 0,
  fulfillment: 0,
  platformCommission: 0,
  returnsRate: 0,
  currency: "COP",
};

function ManualLogisticsModal({
  open,
  onClose,
  funnelId,
  funnelName,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  funnelId: string;
  funnelName: string;
  onSaved: (configured: boolean) => void;
}) {
  const [draft, setDraft] = useState<ManualLogisticsDraft>(emptyDraft);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    financeService.getFinanceManualLogistics(funnelId)
      .then((config) => {
        if (!mounted) return;
        setDraft({
          productCost: config.product_cost,
          shippingCost: config.shipping_cost,
          fulfillment: config.fulfillment_cost,
          platformCommission: config.platform_commission,
          returnsRate: config.return_rate,
          currency: config.currency || "COP",
        });
      })
      .catch(() => {
        if (mounted) toast.error("No pudimos cargar la logística manual");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [funnelId, open]);

  const updateDraft = (key: keyof ManualLogisticsDraft, value: string) => {
    setDraft((current) => ({ ...current, [key]: Number(value || 0) }));
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const saved = await financeService.saveFinanceManualLogistics(funnelId, {
        product_cost: draft.productCost,
        shipping_cost: draft.shippingCost,
        fulfillment_cost: draft.fulfillment,
        platform_commission: draft.platformCommission,
        return_rate: draft.returnsRate,
        currency: draft.currency,
      });
      onSaved(saved.configured);
      toast.success("Datos logísticos guardados para este embudo");
      onClose();
    } catch {
      toast.error("No pudimos guardar la logística manual");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper isOpen={open} onClose={onClose}>
      <div className="p-6">
        <h2 className="mb-2 pr-8 text-lg font-semibold text-gray-900">
          Logística manual de {funnelName}
        </h2>
        <p className="mb-5 text-sm leading-5 text-gray-500">
          Usa estos datos cuando el embudo no tenga Dropi conectado o cuando quieras ajustar costos manualmente.
        </p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <NumberField disabled={loading} label="Costo unitario" value={draft.productCost} onChange={(value) => updateDraft("productCost", value)} />
          <NumberField disabled={loading} label="Costo de envío" value={draft.shippingCost} onChange={(value) => updateDraft("shippingCost", value)} />
          <NumberField disabled={loading} label="Fulfillment" value={draft.fulfillment} onChange={(value) => updateDraft("fulfillment", value)} />
          <NumberField disabled={loading} label="Comisión logística %" value={draft.platformCommission} onChange={(value) => updateDraft("platformCommission", value)} />
          <NumberField disabled={loading} label="Devoluciones %" value={draft.returnsRate} onChange={(value) => updateDraft("returnsRate", value)} />
        </div>

        <div className="mt-6 flex justify-end gap-2 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={saveDraft}
            disabled={loading || saving}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#2b5ce6] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#214ec7] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {saving ? "Guardando..." : "Guardar logística"}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
}

function NumberField({
  label,
  value,
  disabled = false,
  onChange,
}: {
  label: string;
  value: number;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-sm font-semibold text-gray-700">
      {label}
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-gray-900 outline-none focus:border-[#2b5ce6] focus:ring-2 focus:ring-[#2b5ce6]/10"
      />
    </label>
  );
}
